import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  booleanAttribute,
  computed,
  inject,
  input,
  model,
  numberAttribute,
  output,
  signal
} from '@angular/core';
import { OSKValueAccessorBase, provideOSKValueAccessor } from '../../core/forms/osk-value-accessor';
import { OSKColorVariant, OSKIconName, OSKSize } from '../../types/osk-theme.types';
import { OSKIconComponent } from '../osk-icon/osk-icon.component';
import { OSK_UI_LABELS } from '../../core/config/osk-ui-config';

/**
 * Atom: <osk-input>
 * Standard form text input with leading/trailing icons, clear button, password visibility toggle,
 * two-way Signal binding [(value)] AND Angular Reactive/Template Forms (ControlValueAccessor).
 */
@Component({
  selector: 'osk-input',
  standalone: true,
  imports: [OSKIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './osk-input.component.scss',
  providers: [provideOSKValueAccessor(() => OSKInputComponent)],
  template: `
    <div [class]="wrapperClasses()">
      @if (leadingIcon()) {
        <osk-icon class="osk-input__icon" [name]="leadingIcon()!" [size]="15"></osk-icon>
      }

      <input
        class="osk-input__control"
        [attr.id]="inputId() ?? null"
        [attr.name]="name() ?? null"
        [attr.type]="effectiveType()"
        [attr.placeholder]="placeholder()"
        [attr.maxlength]="maxLength() ?? null"
        [attr.autocomplete]="autocomplete() ?? null"
        [disabled]="isDisabled()"
        [readOnly]="readonly()"
        [attr.aria-invalid]="invalid()"
        [value]="value()"
        (input)="onNativeInput($event)"
        (focus)="onNativeFocus($event)"
        (blur)="onNativeBlur($event)"
        (keydown.enter)="enterPressed.emit(value())"
      />

      @if (clearable() && value().length > 0 && !isDisabled() && !readonly()) {
        <button
          type="button"
          class="osk-input__action-btn"
          [attr.aria-label]="text().clear"
          (click)="clearValue()"
        >
          <osk-icon name="x" [size]="13"></osk-icon>
        </button>
      }

      @if (passwordToggle() && type() === 'password') {
        <button
          type="button"
          class="osk-input__action-btn"
          [attr.aria-label]="passwordRevealed() ? text().hidePassword : text().showPassword"
          (click)="togglePasswordVisibility()"
        >
          <osk-icon [name]="passwordRevealed() ? 'eye-off' : 'eye'" [size]="15"></osk-icon>
        </button>
      } @else if (trailingIcon()) {
        <osk-icon class="osk-input__icon" [name]="trailingIcon()!" [size]="15"></osk-icon>
      }
    </div>
  `
})
export class OSKInputComponent extends OSKValueAccessorBase<string> {
  private readonly labels = inject(OSK_UI_LABELS);
  /** Every string this component renders: an input when given, else the configured label. */
  protected readonly text = computed(() => {
    const labels = this.labels().input;
    return {
      clear: labels.clear,
      showPassword: labels.showPassword,
      hidePassword: labels.hidePassword
    };
  });

  readonly value = model<string>('');
  readonly type = input<'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search'>('text');
  readonly placeholder = input<string>('');
  readonly maxLength = input<number | undefined, unknown>(undefined, {
    transform: (v: unknown) => (v === undefined || v === null || v === '' ? undefined : numberAttribute(v))
  });
  readonly inputId = input<string | undefined>(undefined);
  readonly name = input<string | undefined>(undefined);
  readonly autocomplete = input<string | undefined>(undefined);
  readonly leadingIcon = input<OSKIconName | undefined>(undefined);
  readonly trailingIcon = input<OSKIconName | undefined>(undefined);
  readonly clearable = input(false, { transform: booleanAttribute });
  readonly passwordToggle = input(false, { transform: booleanAttribute });
  readonly invalid = input(false, { transform: booleanAttribute });
  override readonly disabled = input(false, { transform: booleanAttribute });
  readonly readonly = input(false, { transform: booleanAttribute });
  readonly size = input<OSKSize>('md');
  readonly color = input<OSKColorVariant>('primary');

  readonly enterPressed = output<string>();
  readonly blurred = output<FocusEvent>();
  readonly focused = output<FocusEvent>();

  protected readonly isFocused = signal<boolean>(false);
  protected readonly passwordRevealed = signal<boolean>(false);

  protected readonly effectiveType = computed<string>(() => {
    if (this.type() === 'password' && this.passwordRevealed()) {
      return 'text';
    }
    return this.type();
  });

  protected readonly wrapperClasses = computed<string>(() => {
    const classes: string[] = [
      'osk-input',
      `osk-input--${this.color()}`,
      `osk-input--${this.size()}`
    ];
    if (this.isFocused()) {
      classes.push('osk-input--focused');
    }
    if (this.invalid()) {
      classes.push('osk-input--invalid');
    }
    if (this.isDisabled()) {
      classes.push('osk-input--disabled');
    }
    return classes.join(' ');
  });

  onNativeInput(event: Event): void {
    const nextValue = (event.target as HTMLInputElement).value;
    this.value.set(nextValue);
    this.onChangeFn(nextValue);
  }

  onNativeFocus(event: FocusEvent): void {
    this.isFocused.set(true);
    this.focused.emit(event);
  }

  onNativeBlur(event: FocusEvent): void {
    this.isFocused.set(false);
    this.onTouchedFn();
    this.blurred.emit(event);
  }

  clearValue(): void {
    this.value.set('');
    this.onChangeFn('');
  }

  togglePasswordVisibility(): void {
    this.passwordRevealed.update((v) => !v);
  }

  override writeValue(val: string | null | undefined): void {
    this.value.set(val ?? '');
  }
}
